import React, { useEffect, useState } from "react";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";

import DialogContent from "@material-ui/core/DialogContent";
import {
  compose,
  equals,
  find,
  lensPath,
  mergeRight,
  pick,
  prop,
  propEq,
  set,
  sortBy,
  toLower,
} from "ramda";
import Grid from "@material-ui/core/Grid";
import { diffObject, isBlank, notBlank } from "../../util/ext";
import {
  useOrganizationApplicationCreate,
  useOrganizationApplicationDetail,
  useOrganizationApplicationDetailCreate,
  useOrganizationApplicationDetailUpdate,
} from "../organizationServices";
import {
  validateApplicationAccess,
  validateApplicationAccessField,
} from "../organizationValidation";
import { useErrorBox } from "./use-errorBox";
import { useApplicationAccounts } from "../../manage-application-accounts/applicationAccountServices";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/styles";
import { Skeleton } from "@material-ui/lab";
import Autocomplete from "@material-ui/lab/Autocomplete";

const useStyles = makeStyles(theme => ({
  title: {
    fontSize: "1.8rem",
  },
}));

const ApplicationAccessDialog = ({
  organizationId,
  applicationId,
  open,
  onCancel,
  onSave,
}) => {
  const classes = useStyles();

  const orgAppDetailResponse = useOrganizationApplicationDetail(
    organizationId,
    applicationId,
  );
  const [fields, setFields] = useState({});
  const [details, setDetails] = useState({});
  useEffect(() => {
    if (orgAppDetailResponse.isSuccess && notBlank(orgAppDetailResponse.data)) {
      const accessDetails = pick(
        ["applicationUrl", "welcomeEmailSubject", "welcomeEmailTemplate"],
        orgAppDetailResponse.data,
      );
      setDetails(accessDetails);
      setFields(accessDetails);
    } else {
      setDetails({});
      setFields({});
    }
  }, [
    applicationId,
    orgAppDetailResponse.isSuccess,
    orgAppDetailResponse.data,
  ]);

  const [selectedApplication, setSelectedApplication] = useState(null);
  const handleApplicationChange = (_event, application) => {
    setSelectedApplication(application);
  };

  const [applications, setApplications] = useState([]);
  const appAcctResponse = useApplicationAccounts();
  useEffect(() => {
    if (open && appAcctResponse.isSuccess) {
      const sortedApplications = sortBy(
        compose(toLower, prop("applicationName")),
        appAcctResponse.data,
      );

      setApplications(sortedApplications);
      if (notBlank(applicationId)) {
        const application = find(
          propEq("applicationId", applicationId),
          sortedApplications,
        );
        setSelectedApplication(application);
      }
    }
  }, [open, applicationId, appAcctResponse.isSuccess, appAcctResponse.data]);

  const {
    addValidationError,
    removeValidationError,
    setValidationErrors,
    setGeneralError,
    hasValidationError,
    firstValidationError,
    ErrorBox: DialogErrors,
  } = useErrorBox();

  const handleTextChange = path => event => {
    const value = event.target.value;
    setFields(prev => set(lensPath(path), value, prev));
  };

  const handleBlur = (field, value) => {
    const validationErrors = validateApplicationAccessField(field, value);
    if (notBlank(validationErrors)) {
      addValidationError(validationErrors);
    } else {
      removeValidationError(field);
    }
  };

  const applicationAccessCreate = useOrganizationApplicationCreate();
  const applicationAccessDetailCreate = useOrganizationApplicationDetailCreate();
  const applicationAccessDetailUpdate = useOrganizationApplicationDetailUpdate();
  const handleApply = async () => {
    const validationErrors = validateApplicationAccess(fields);
    const applicationCheck = isBlank(selectedApplication)
      ? { applicationId: ["An Application must be selected."] }
      : {};
    const allErrors = mergeRight(validationErrors, applicationCheck);

    if (notBlank(allErrors)) {
      setValidationErrors(allErrors);
    } else {
      try {
        if (isBlank(applicationId)) {
          await applicationAccessCreate.mutateAsync({
            resourceIds: {
              orgId: organizationId,
              appId: selectedApplication.applicationId,
            },
          });
        }

        const diff = diffObject(details, fields);
        if (notBlank(diff)) {
          if (notBlank(applicationId)) {
            await applicationAccessDetailUpdate.mutateAsync({
              resourceIds: { orgId: organizationId, appId: applicationId },
              data: fields,
            });
          } else {
            await applicationAccessDetailCreate.mutateAsync({
              resourceIds: {
                orgId: organizationId,
                appId: selectedApplication.applicationId,
              },
              data: fields,
            });
          }
        }

        onSave(fields);
      } catch (err) {
        setGeneralError(err);
      }
    }
  };

  return (
    <Dialog open={open}>
      <DialogTitle id="form-dialog-title">Application Access</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Select an Application and supply optional details.
        </DialogContentText>
        <DialogErrors />
        <Grid container spacing={4}>
          <Grid item xs={12}>
            {appAcctResponse.isLoading ? (
              <Skeleton variant="text" />
            ) : (
              <Autocomplete
                disabled={notBlank(applicationId)}
                options={applications}
                getOptionLabel={app =>
                  notBlank(app) && `${app.applicationName}`
                }
                getOptionSelected={(opt, val) =>
                  notBlank(val) && equals(opt.applicationId, val.applicationId)
                }
                value={selectedApplication}
                onChange={handleApplicationChange}
                renderInput={params => {
                  params.inputProps["aria-autocomplete"] = "none";
                  params.inputProps["id"] = null;
                  return (
                    <TextField
                      {...params}
                      type="text"
                      name={"application-" + Math.random()}
                      label="Application"
                      fullWidth
                    />
                  );
                }}
              />
            )}
          </Grid>

          <Grid item xs={12}>
            <TextField
              id="application-url"
              fullWidth
              label="Application URL"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.applicationUrl || ""}
              onChange={handleTextChange(["applicationUrl"])}
              error={hasValidationError("applicationUrl")}
              helperText={firstValidationError("applicationUrl")}
              onBlur={() => handleBlur("applicationUrl", fields.applicationUrl)}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              id="welcome-email-subject"
              fullWidth
              label="Welcome Email Subject"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.welcomeEmailSubject || ""}
              onChange={handleTextChange(["welcomeEmailSubject"])}
              error={hasValidationError("welcomeEmailSubject")}
              helperText={firstValidationError("welcomeEmailSubject")}
              onBlur={() =>
                handleBlur("welcomeEmailSubject", fields.welcomeEmailSubject)
              }
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              id="welcome-email-template"
              fullWidth
              label="Welcome Email Template"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.welcomeEmailTemplate || ""}
              onChange={handleTextChange(["welcomeEmailTemplate"])}
              error={hasValidationError("welcomeEmailTemplate")}
              helperText={firstValidationError("welcomeEmailTemplate")}
              onBlur={() =>
                handleBlur("welcomeEmailTemplate", fields.welcomeEmailTemplate)
              }
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApplicationAccessDialog;
